import { Flex, Text } from '@ifood/pomodoro-components';
import tokens from '@ifood/pomodoro-tokens';
import { FormattedMessage } from 'gatsby-plugin-intl';
import pipe from 'ramda/es/pipe';
import React, { FC, useRef } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';

import { ROUTER_PATHS } from '@constants/router';
import { useIsPageAt } from 'hooks/is-at';
import { Link } from 'modules/UI';
import { noop } from 'utils/js';
import { getURLAnchor } from 'utils/string';

import { HeaderItem } from '../../types';
import {
  Container,
  Item,
  List,
  MiniLink,
  ModuleItem,
  ModulesContent,
  ReferenceContainer,
  SubLinksContent,
} from './styles';

type PropTypes = {
  items: HeaderItem[];
  open: boolean;
  onClose: () => void;
  mobile?: boolean;
  id: string;
  className?: string;
  hasModules?: boolean;
};

const API_MODULES = [
  {
    module: 'Authentication',
    guides: ROUTER_PATHS.GUIDES_AUTHENTICATION,
    reference: ROUTER_PATHS.REFERENCES_AUTHENTICATION,
  },
  { module: 'Merchant', guides: ROUTER_PATHS.GUIDES_MERCHANT, reference: ROUTER_PATHS.REFERENCES_MERCHANT },
  { module: 'Order', guides: ROUTER_PATHS.GUIDES_ORDER, reference: ROUTER_PATHS.REFERENCES_ORDER },
  { module: 'Shipping', guides: ROUTER_PATHS.GUIDES_SHIPPING, reference: ROUTER_PATHS.REFERENCES_SHIPPING },
  { module: 'Catalog', guides: ROUTER_PATHS.GUIDES_CATALOG, reference: ROUTER_PATHS.REFERENCES_CATALOG },
  { module: 'Financial', guides: ROUTER_PATHS.GUIDES_FINANCIAL, reference: ROUTER_PATHS.REFERENCES_FINANCIAL },
  { module: 'Review', guides: ROUTER_PATHS.GUIDES_REVIEW, reference: ROUTER_PATHS.REFERENCES_REVIEW },
  { module: 'Groceries', guides: ROUTER_PATHS.GUIDES_GROCERIES, reference: ROUTER_PATHS.REFERENCES_GROCERIES },
];

const HeaderMenu: FC<PropTypes> = ({ open, onClose, items, id, mobile = false, className, hasModules = false }) => {
  const ref = useRef<HTMLDivElement>(null);

  useOnclickOutside(
    ref,
    () => {
      if (open) onClose();
    },
    { ignoreClass: id }
  );

  const isAtReferences = useIsPageAt(ROUTER_PATHS.REFERENCES);

  return open ? (
    <Container ref={ref} className={className} isMobile={mobile}>
      <Flex flexDirection="column" width="max-content" pr={tokens.space.large}>
        {hasModules && (
          <Text fontWeight="bold" fontFamily={tokens.font.family.ifood.body} mb={tokens.space.large}>
            <FormattedMessage id="core.header.docs" />
          </Text>
        )}
        <List>
          {items.map(({ href, messageId, onClick = noop }) => (
            <Link
              href={href}
              key={messageId}
              partiallyActive
              onClick={pipe(onClick, onClose)}
              dataTestId={`nav-link-${messageId}`}
            >
              <Item>
                <FormattedMessage id={messageId} />
              </Item>
            </Link>
          ))}
        </List>
      </Flex>
      {hasModules && (
        <ModulesContent isMobile={mobile}>
          <Text fontWeight="bold" fontFamily={tokens.font.family.ifood.body} mb={tokens.space.large}>
            <FormattedMessage id="core.header.docs.modules" />
          </Text>
          <List>
            {API_MODULES.map(({ guides, module, reference }) => (
              <ModuleItem key={module}>
                <Link href={guides} partiallyActive dataTestId={`nav-link-${module}`}>
                  <Item>{module}</Item>
                </Link>
                <SubLinksContent>
                  <MiniLink onClick={onClose} href={guides} dataTestId={`nav-link-${module}`}>
                    <FormattedMessage id="core.header.docs.guides" />
                  </MiniLink>
                  <ReferenceContainer>
                    <MiniLink
                      onClick={onClose}
                      href={isAtReferences ? getURLAnchor(reference) : reference}
                      dataTestId={`nav-link-${reference}`}
                    >
                      <FormattedMessage id="core.header.docs.api-reference" />
                    </MiniLink>
                  </ReferenceContainer>
                </SubLinksContent>
              </ModuleItem>
            ))}
          </List>
        </ModulesContent>
      )}
    </Container>
  ) : null;
};

export default HeaderMenu;
